import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, X, FileText, CalendarDays, Loader2 } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Context — lets any button on the site open the modal.
// ─────────────────────────────────────────────────────────────
const LetsTalkCtx = createContext(null);

export function LetsTalkProvider({ children }) {
  const [view, setView] = useState(null); // null | 'menu' | 'typeform' | 'calendly'

  const open = useCallback((initial = "menu") => setView(initial), []);
  const close = useCallback(() => setView(null), []);

  // ESC to close
  useEffect(() => {
    if (!view) return;
    const onKey = (e) => e.key === "Escape" && setView(null);
    window.addEventListener("keydown", onKey);
    // Lock body scroll + pause Lenis while modal is up
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      if (window.__lenis) window.__lenis.start();
    };
  }, [view]);

  return (
    <LetsTalkCtx.Provider value={{ view, open, close, setView }}>
      {children}
      <LetsTalkModal view={view} setView={setView} close={close} />
    </LetsTalkCtx.Provider>
  );
}

export function useLetsTalk() {
  const ctx = useContext(LetsTalkCtx);
  if (!ctx) throw new Error("useLetsTalk must be used inside <LetsTalkProvider>");
  return ctx;
}

// ─────────────────────────────────────────────────────────────
// Modal shell
// ─────────────────────────────────────────────────────────────
const TYPEFORM_URL = "https://form.typeform.com/to/n8iVIftc";
const CALENDLY_URL = "https://calendly.com/hello-run-loop/runloop-brainstorm";

function LetsTalkModal({ view, setView, close }) {
  const open = view !== null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lets-talk-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 md:px-8"
          data-testid="lets-talk-modal"
        >
          {/* Backdrop */}
          <button
            aria-label="Close"
            onClick={close}
            data-testid="lets-talk-backdrop"
            className="absolute inset-0 bg-ink/45 backdrop-blur-md"
          />

          {/* Panel */}
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`relative w-full rounded-[1.75rem] bg-surface text-content shadow-[0_40px_120px_-30px_rgba(0,0,0,0.55)] ring-1 ring-line ${
              view === "menu" ? "max-w-2xl" : "max-w-4xl"
            }`}
          >
            {view === "menu" && <MenuView setView={setView} close={close} />}
            {view === "typeform" && <TypeformView setView={setView} close={close} />}
            {view === "calendly" && <CalendlyView setView={setView} close={close} />}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────
// Views
// ─────────────────────────────────────────────────────────────
function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="lets-talk-close"
      aria-label="Close"
      className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full text-content/70 transition-colors hover:bg-page hover:text-content"
    >
      <X size={18} strokeWidth={2} />
    </button>
  );
}

function BackBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="lets-talk-back"
      className="inline-flex items-center gap-2 rounded-full border border-line px-3 py-1.5 font-mono-c text-[0.66rem] uppercase tracking-[0.16em] text-content/70 transition-colors hover:bg-page hover:text-content"
    >
      <ArrowLeft size={14} strokeWidth={2} />
      Back
    </button>
  );
}

function OptionCard({ icon: Icon, title, desc, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="group relative flex w-full items-center gap-5 rounded-[1.15rem] border border-line bg-page/70 px-5 py-5 text-left transition-all duration-300 hover:-translate-y-[2px] hover:border-brand/60 hover:bg-page md:px-6 md:py-6"
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand/15 text-content/80 ring-1 ring-brand/25">
        <Icon size={18} strokeWidth={1.8} />
      </span>
      <span className="flex-1">
        <span className="block font-display text-lg font-semibold text-content md:text-xl">{title}</span>
        <span className="mt-1 block font-body text-sm italic text-content/60 md:text-[0.95rem]">{desc}</span>
      </span>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-brand transition-transform duration-300 group-hover:translate-x-1 group-hover:rotate-45">
        <ArrowUpRight size={20} strokeWidth={2.2} />
      </span>
    </button>
  );
}

function MenuView({ setView, close }) {
  return (
    <div className="relative px-6 py-9 md:px-11 md:py-12">
      <CloseBtn onClick={close} />
      <h2 className="font-display text-4xl font-light tracking-[-0.015em] text-content md:text-5xl">
        <span className="font-light">Let&apos;s</span>{" "}
        <span className="emph italic">talk.</span>
      </h2>
      <p className="mt-3 max-w-none whitespace-nowrap font-body text-[0.9rem] leading-relaxed text-content/65 md:text-[0.95rem]">
        Tell us as much or as little as you want. We&apos;ll take it from there.
      </p>

      <div className="mt-8 flex flex-col gap-3.5">
        <OptionCard
          icon={FileText}
          title="Introduce yourself"
          desc="Tell us who you are and how things work right now."
          testid="lets-talk-typeform"
          onClick={() => setView("typeform")}
        />
        <OptionCard
          icon={CalendarDays}
          title="Brainstorm with us"
          desc="Pick a time for a 30 min discovery call."
          testid="lets-talk-calendly"
          onClick={() => setView("calendly")}
        />
      </div>
    </div>
  );
}

function EmbedHeader({ title, setView, close }) {
  return (
    <div className="flex items-center justify-between border-b border-line px-6 py-4 md:px-8">
      <div className="flex items-center gap-4">
        <BackBtn onClick={() => setView("menu")} />
        <span className="font-display text-base font-medium text-content md:text-lg">{title}</span>
      </div>
      <button
        onClick={close}
        data-testid="lets-talk-close"
        aria-label="Close"
        className="grid h-9 w-9 place-items-center rounded-full text-content/70 transition-colors hover:bg-page hover:text-content"
      >
        <X size={18} strokeWidth={2} />
      </button>
    </div>
  );
}

function IframeSkeleton() {
  return (
    <div className="absolute inset-0 grid place-items-center bg-surface">
      <div className="flex flex-col items-center gap-3 text-content/60">
        <Loader2 size={22} className="animate-spin" />
        <span className="font-mono-c text-[0.68rem] uppercase tracking-[0.18em]">Loading</span>
      </div>
    </div>
  );
}

function TypeformView({ setView, close }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative">
      <EmbedHeader title="Introduce yourself" setView={setView} close={close} />
      <div className="relative h-[75vh] max-h-[720px] w-full overflow-hidden rounded-b-[1.75rem]">
        {!loaded && <IframeSkeleton />}
        <iframe
          title="Introduce yourself"
          src={`${TYPEFORM_URL}?typeform-embed=embed-widget&embed-hide-headers=true&embed-hide-footer=true&embed-opacity=0`}
          onLoad={() => setLoaded(true)}
          className="h-full w-full border-0 bg-surface"
          allow="camera; microphone; autoplay; encrypted-media;"
          data-testid="typeform-iframe"
        />
      </div>
    </div>
  );
}

function CalendlyView({ setView, close }) {
  const [loaded, setLoaded] = useState(false);
  // Calendly embed params — tune colors to match our navy/brand aesthetic
  const params = new URLSearchParams({
    embed_domain: typeof window !== "undefined" ? window.location.host : "runloop.local",
    embed_type: "Inline",
    hide_gdpr_banner: "1",
    hide_landing_page_details: "1",
    background_color: "F6F3EE",
    text_color: "1A2333",
    primary_color: "1A2333",
  });
  const src = `${CALENDLY_URL}?${params.toString()}`;

  return (
    <div className="relative">
      <EmbedHeader title="Brainstorm with us" setView={setView} close={close} />
      <div className="relative h-[78vh] max-h-[760px] w-full overflow-hidden rounded-b-[1.75rem] bg-page">
        {!loaded && <IframeSkeleton />}
        <iframe
          title="Book a brainstorm"
          src={src}
          onLoad={() => setLoaded(true)}
          className="h-full w-full border-0"
          data-testid="calendly-iframe"
        />
      </div>
    </div>
  );
}
